import Icon from "./Icon";
import { useRouter } from "next/router";
import { Col, Switch, InputUpdate } from "@dreambigger/design-system/src";
import React, { useState } from "react";
import { capitalizeFirstLetter } from "../../utils/textFormatting";

export interface MenuItem extends SubMenuItem {
  submenu?: SubMenuItem[];
}

export interface SubMenuItem {
  id: string;
  name: string;
  icon?: string;
  path?: string; // Used when navigating within app
  externalUrl?: string; // Used when navigating to an external site
  suffix?: React.ReactNode;
  suffixClassName?: string;
  onClick?: any;
  hoverTitle?: string;
}

type MenuProps = {
  title?: string;
  titleClassName?: string;
  categories?: string[];
  items?: MenuItem[];
  activeItem?: string;
  onSelect?: any;
  className?: string;
  itemsClassName?: string;
  filterLabel?: string;
  filterInitiallyChecked?: boolean;
  onFilterChange?: any;
  addItemLabel?: string;
  addItemPlaceholder?: string;
  onAdd?: any;
  // When set, the add button calls this directly instead of revealing an
  // inline text input. Useful when the add flow opens a modal/wizard.
  onAddClick?: () => void;
  emptyStateMessage?: string | React.ReactNode;
};

const Menu = ({
  title,
  titleClassName = "f-5 fwb pa-3",
  items = [],
  activeItem,
  onSelect,
  className,
  itemsClassName = "f-4 pv-2 ph-3",
  filterLabel,
  filterInitiallyChecked,
  onFilterChange,
  addItemLabel,
  addItemPlaceholder,
  onAdd,
  onAddClick,
  emptyStateMessage = <span className="fsi gray-6">No Data</span>,
}: MenuProps) => {
  const [editing, setEditing] = useState(false);
  const router = useRouter();
  const menuClasses = `${itemsClassName} pointer hlight-primary`;
  const inactiveMenuClasses = `${itemsClassName}`;

  const handleClick = (item: MenuItem) => {
    // If externalUrl, redirect to new link
    if (item.externalUrl) {
      window.location.href = item.externalUrl;
      return;
    }
    // If path, redirect to new page within app
    if (item.path) router.push(item.path);
    // onSelect runs whenever an item is clicked. If using setActiveItem, include this in onSelect
    if (onSelect) onSelect(item.id);
    // Menu items can optionally also be given an onClick function to run custom code
    if (item.onClick) item.onClick(item.id);
  };

  const mapItem = (item: MenuItem, subitem?: boolean) => {
    const {
      name,
      icon,
      id,
      suffix,
      suffixClassName,
      submenu,
      onClick,
      externalUrl,
      path,
      hoverTitle,
    } = item;
    return (
      <div
        key={id}
        title={hoverTitle}
        onClick={() => handleClick(item)}
        // Item should be highlighted if it is the active item
        className={`flex items-center ${
          activeItem === item.id &&
          "flex items-center bg-primary-light-4 primary"
        }
        ${
          // Indent if submenu item
          subitem ? "pl-5" : ""
        } 
        ${
          // If there is an attached submenu and no attached function, then the item will have no mouseover effect.
          submenu && !onClick && !externalUrl && !path
            ? inactiveMenuClasses
            : menuClasses
        }`}
      >
        {icon && <Icon name={icon} className="mr-3" />}
        {capitalizeFirstLetter(name)}
        {suffix && <span className={suffixClassName}>{suffix}</span>}
      </div>
    );
  };

  return (
    <Col className={className}>
      {/* TITLE */}
      {title && (
        <h3 className={titleClassName}>{capitalizeFirstLetter(title)}</h3>
      )}
      {/* FILTER */}
      {filterLabel && (
        <div className="flex justify-between pa-3 mb-3 bg-gray-4">
          <span className="f-2 gray-7">{filterLabel}</span>{" "}
          <Switch
            size="small"
            onChange={onFilterChange}
            checked={filterInitiallyChecked}
          />
        </div>
      )}
      {/* MENU ITEMS */}
      {/* If there are no items, such as when creating a new menu list, display emptyStateMessage */}
      {items.length === 0 && <div className="pa-3">{emptyStateMessage}</div>}
      {items?.map((item) => {
        // If the item has a submenu, map the submenu items below it before continuing.
        const { submenu } = item;
        if (submenu) {
          return (
            <div key={item.id}>
              <div className="gray-7 mv-1">{mapItem(item)}</div>
              {submenu.map((subItem) => mapItem(subItem, true))}
            </div>
          );
        }
        // If the item has no submenu, it should be mapped as a regular item.
        return mapItem(item);
      })}

      {/* ADD ITEM  - Allows the user to add an item to the menu. Submenu items cannot currently be added. */}
      {/* It is recommended that the onAdd function that is passed in also updates the state of items */}
      {addItemLabel && editing === false && (
        <button
          className={`${menuClasses} bg-transparent bn pv-3`}
          onClick={() => (onAddClick ? onAddClick() : setEditing(true))}
        >
          {addItemLabel}
        </button>
      )}
      {editing === true && (
        <InputUpdate
          className="pa-3"
          placeHolder={addItemPlaceholder}
          onSave={(enteredText: string) => onAdd(enteredText)}
          onCancel={() => setEditing(false)}
        />
      )}
    </Col>
  );
};

export default Menu;
